<!--
  Persistent "Raise an Issue" button -- mounted once in LandingPage.vue
  (the shell every routed page lives inside), so it's visible everywhere,
  not just on one screen. Captures a screenshot of exactly what the
  reporter was looking at (html2canvas, no permission prompt, unlike the
  native screen-capture API) BEFORE the dialog itself opens, so the
  dialog never ends up in its own screenshot. Files a real Jira ticket in
  project KAN -- see reportPlatformIssue (yandle_admin_portal_1).
-->
<template>
  <button
    type="button"
    class="riw-trigger"
    :class="{ 'riw-trigger--busy': capturing }"
    :disabled="capturing"
    title="Raise an issue"
    @click="openDialog"
  >
    <v-progress-circular v-if="capturing" indeterminate size="18" width="2" color="white" />
    <v-icon v-else icon="mdi-bug-outline" size="20" />
  </button>

  <v-dialog v-model="dialogOpen" max-width="520" persistent scrollable>
    <v-card class="riw-card">
      <v-card-title class="riw-card__title">
        <span>{{ resultData ? 'Issue reported' : 'Raise an issue' }}</span>
        <button type="button" class="riw-close" aria-label="Close" @click="closeDialog">
          <v-icon icon="mdi-close" size="18" />
        </button>
      </v-card-title>

      <v-card-text class="riw-card__body">
        <template v-if="resultData">
          <div class="riw-success">
            <v-icon icon="mdi-check-circle-outline" size="40" color="success" />
            <p class="riw-success__text">
              Filed as <strong>{{ resultData.issue_key }}</strong>. Thanks for the report.
            </p>
            <a
              :href="resultData.issue_url"
              target="_blank"
              rel="noopener noreferrer"
              class="riw-success__link"
            >
              View in Jira
              <v-icon icon="mdi-open-in-new" size="14" />
            </a>
          </div>
        </template>

        <template v-else>
          <div v-if="screenshotDataUrl" class="riw-preview">
            <img :src="screenshotDataUrl" alt="Screenshot preview" class="riw-preview__img" />
            <span class="riw-preview__caption">Attached automatically -- this is what you were looking at</span>
          </div>
          <p v-else-if="screenshotFailed" class="riw-preview__failed">
            Couldn't capture a screenshot -- your report will still be filed without one.
          </p>

          <div class="riw-type-toggle" role="radiogroup" aria-label="Issue type">
            <button
              type="button"
              class="riw-type-btn"
              :class="{ 'riw-type-btn--active': issueType === 'bug' }"
              :disabled="submitting"
              @click="issueType = 'bug'"
            >
              <v-icon icon="mdi-bug-outline" size="15" />
              Bug
            </button>
            <button
              type="button"
              class="riw-type-btn"
              :class="{ 'riw-type-btn--active': issueType === 'enhancement' }"
              :disabled="submitting"
              @click="issueType = 'enhancement'"
            >
              <v-icon icon="mdi-lightbulb-on-outline" size="15" />
              Enhancement
            </button>
          </div>

          <label class="riw-field">
            <span class="riw-field__label">{{ issueType === 'enhancement' ? "What's the idea? *" : "What's wrong? *" }}</span>
            <input
              v-model="summary"
              type="text"
              class="riw-input"
              :placeholder="issueType === 'enhancement'
                ? 'Short summary, e.g. \'Add a product filter to the Knowledge Base page\''
                : 'Short summary, e.g. \'Refresh button duplicated on Call History tab\''"
              maxlength="200"
              :disabled="submitting"
            />
          </label>

          <label class="riw-field">
            <span class="riw-field__label">Details (optional)</span>
            <textarea
              v-model="description"
              class="riw-textarea"
              rows="4"
              placeholder="Anything else that would help -- what you expected, steps to reproduce, etc."
              :disabled="submitting"
            />
          </label>

          <p v-if="errorMessage" class="riw-error">{{ errorMessage }}</p>
        </template>
      </v-card-text>

      <v-card-actions v-if="!resultData" class="riw-card__actions">
        <button type="button" class="riw-btn riw-btn--ghost" :disabled="submitting" @click="closeDialog">
          Cancel
        </button>
        <button
          type="button"
          class="riw-btn riw-btn--primary"
          :disabled="submitting || !summary.trim()"
          @click="submit"
        >
          <v-progress-circular v-if="submitting" indeterminate size="16" width="2" color="white" />
          <span>{{ submitting ? 'Filing…' : 'Submit' }}</span>
        </button>
      </v-card-actions>
      <v-card-actions v-else class="riw-card__actions">
        <button type="button" class="riw-btn riw-btn--primary" @click="closeDialog">Done</button>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref } from 'vue'
import { reportPlatformIssue } from '@/api/reportIssueApi'
import { isApiSuccess, getApiMessage } from '@/mixins/Extras/ApiResponse'

defineOptions({ name: 'ReportIssueWidget' })

const dialogOpen = ref(false)
const capturing = ref(false)
const submitting = ref(false)
const screenshotDataUrl = ref('')
const screenshotFailed = ref(false)
const issueType = ref('bug')
const summary = ref('')
const description = ref('')
const errorMessage = ref('')
const resultData = ref(null)

function resetForm() {
  screenshotDataUrl.value = ''
  screenshotFailed.value = false
  issueType.value = 'bug'
  summary.value = ''
  description.value = ''
  errorMessage.value = ''
  resultData.value = null
}

async function openDialog() {
  resetForm()
  capturing.value = true
  try {
    // Dynamic import -- html2canvas is only ever needed here, no reason to
    // put it in the app's main bundle for every page load.
    const { default: html2canvas } = await import('html2canvas')
    const canvas = await html2canvas(document.body, {
      logging: false,
      useCORS: true,
      // Downscale -- a full-resolution capture of a dense data table is
      // both a bigger Jira attachment than needed and slower to render
      // here for a preview nobody is zooming into pixel-by-pixel.
      scale: Math.min(1, window.devicePixelRatio > 1 ? 1 : 1.5),
    })
    screenshotDataUrl.value = canvas.toDataURL('image/png')
  } catch (err) {
    console.error('Screenshot capture failed (non-fatal, reporting without one)', err)
    screenshotFailed.value = true
  } finally {
    capturing.value = false
    dialogOpen.value = true
  }
}

function closeDialog() {
  dialogOpen.value = false
}

async function submit() {
  if (!summary.value.trim() || submitting.value) return
  submitting.value = true
  errorMessage.value = ''

  const response = await reportPlatformIssue({
    summary: summary.value,
    description: description.value,
    issueType: issueType.value,
    screenshotBase64: screenshotDataUrl.value,
    pageTitle: document.title,
    pageUrl: window.location.href,
  })

  submitting.value = false

  if (isApiSuccess(response)) {
    resultData.value = response.data
  } else {
    errorMessage.value = getApiMessage(response, 'Failed to report the issue. Please try again.')
  }
}
</script>

<style scoped>
.riw-trigger {
  position: fixed;
  right: 1.5rem;
  bottom: 1.5rem;
  /* Vuetify overlays (v-dialog, including fullscreen ones) default to a
     z-index around 2400+ and climb higher with each stacked overlay. This
     button has to stay reachable no matter what's open on screen -- a
     full-screen dialog, a nested dialog inside a dialog, a menu -- since
     "report an issue with what I'm looking at" is exactly when someone is
     most likely to be inside one of those. Comfortably above any realistic
     Vuetify stack depth. */
  z-index: 2147483000;
  width: 3rem;
  height: 3rem;
  border-radius: 999px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--tw-accent, #4f46e5);
  color: #fff;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.22);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.riw-trigger:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.28);
}

.riw-trigger--busy {
  cursor: wait;
}

.riw-card {
  border-radius: 0.85rem;
}

.riw-card__title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 1.05rem;
  font-weight: 700;
  padding: 1.1rem 1.25rem 0.75rem;
}

.riw-close {
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--tw-muted, #6b7280);
  display: flex;
}

.riw-card__body {
  padding: 0 1.25rem 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.riw-preview {
  border: 1px solid var(--tw-border, rgba(0, 0, 0, 0.1));
  border-radius: 0.6rem;
  overflow: hidden;
}

.riw-preview__img {
  display: block;
  width: 100%;
  max-height: 11rem;
  object-fit: cover;
  object-position: top;
}

.riw-preview__caption {
  display: block;
  padding: 0.4rem 0.6rem;
  font-size: 0.75rem;
  color: var(--tw-muted, #6b7280);
}

.riw-preview__failed {
  margin: 0;
  font-size: 0.82rem;
  color: var(--tw-muted, #6b7280);
}

.riw-type-toggle {
  display: flex;
  gap: 0.5rem;
}

.riw-type-btn {
  flex: 1 1 0%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  border: 1px solid var(--tw-border, rgba(0, 0, 0, 0.15));
  border-radius: 0.5rem;
  padding: 0.5rem 0.6rem;
  font-size: 0.85rem;
  font-weight: 600;
  background: transparent;
  color: var(--tw-muted, #6b7280);
  cursor: pointer;
  transition: border-color 0.12s ease, color 0.12s ease, background 0.12s ease;
}

.riw-type-btn--active {
  border-color: var(--tw-accent, #4f46e5);
  color: var(--tw-accent, #4f46e5);
  background: color-mix(in srgb, var(--tw-accent, #4f46e5) 8%, transparent);
}

.riw-field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.riw-field__label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--tw-text, #111827);
}

.riw-input,
.riw-textarea {
  border: 1px solid var(--tw-border, rgba(0, 0, 0, 0.15));
  border-radius: 0.5rem;
  padding: 0.55rem 0.7rem;
  font-size: 0.88rem;
  font-family: inherit;
  color: var(--tw-text, #111827);
  background: var(--tw-surface, #fff);
  resize: vertical;
}

.riw-input:focus,
.riw-textarea:focus {
  outline: none;
  border-color: var(--tw-accent, #4f46e5);
}

.riw-error {
  margin: 0;
  font-size: 0.82rem;
  color: #dc2626;
}

.riw-card__actions {
  padding: 0.9rem 1.25rem 1.15rem;
  gap: 0.6rem;
}

.riw-btn {
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.86rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid transparent;
}

.riw-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.riw-btn--ghost {
  background: transparent;
  border-color: var(--tw-border, rgba(0, 0, 0, 0.15));
  color: var(--tw-text, #111827);
}

.riw-btn--primary {
  background: var(--tw-accent, #4f46e5);
  color: #fff;
}

.riw-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.5rem;
  padding: 1rem 0;
}

.riw-success__text {
  margin: 0;
  font-size: 0.92rem;
  color: var(--tw-text, #111827);
}

.riw-success__link {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  color: var(--tw-accent, #4f46e5);
  font-weight: 600;
  font-size: 0.86rem;
  text-decoration: none;
}

.riw-success__link:hover {
  text-decoration: underline;
}
</style>
